import { useState, useEffect, useCallback, lazy, Suspense, ReactNode } from 'react';
import { AuthModalContext } from './auth-modal-context';

type AuthView = 'signin' | 'signup' | 'reset-password' | 'two-factor';

const AuthModal = lazy(() =>
  import('@/components/auth/AuthModal').then((m) => ({ default: m.AuthModal }))
);

export function AuthModalProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [hasBeenOpened, setHasBeenOpened] = useState(false);
  const [initialView, setInitialView] = useState<AuthView>('signin');

  // Detect password reset hash once on mount, before Supabase clears it
  useEffect(() => {
    const hash = window.location.hash;
    if (hash.includes('access_token') && hash.includes('type=recovery')) {
      setInitialView('reset-password');
      setHasBeenOpened(true);
      setIsOpen(true);
    }
  }, []);

  const showAuthModal = useCallback(() => {
    setInitialView('signin');
    setHasBeenOpened(true);
    setIsOpen(true);
  }, []);

  const hideAuthModal = useCallback(() => {
    setIsOpen(false);
  }, []);

  return (
    <AuthModalContext.Provider value={{ showAuthModal, hideAuthModal }}>
      {children}
      {hasBeenOpened && (
        <Suspense fallback={null}>
          <AuthModal isOpen={isOpen} onClose={hideAuthModal} initialView={initialView} />
        </Suspense>
      )}
    </AuthModalContext.Provider>
  );
}
